import React, { useEffect, useState, useMemo } from 'react';
import RecipeCard from './RecipeCard';
import Masonry from 'react-masonry-css';
import { Spinner } from 'react-bootstrap';
import { useGetAllFavoriteRecipes } from '../hooks/favoriteRecipe';
import { useGetAllDislikedRecipes } from '../hooks/dislikeRecipe';
import { fetchWithCSRF } from '../utils/csrfUtils';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';
import AddPantryIngredientButton from './AddPantryIngredientButton';
import { calculatePantryMatch } from '../utils/pantryMatchCalculator';

const RecipesForPantry = ({ searchTerm = '', onMealAdded }) => {
    const [recipes, setRecipes] = useState([]);
    const [loading, setLoading] = useState(true);
    const { getAllFavoriteRecipes } = useGetAllFavoriteRecipes();
    const { getAllDislikedRecipes } = useGetAllDislikedRecipes();
    const [favoriteRecipes, setFavoriteRecipes] = useState([]);
    const [dislikedRecipes, setDislikedRecipes] = useState([]);
    const [pantryIngredients, setPantryIngredients] = useState(null);
    const { sessionToken, pgUser } = useAuth();
    const navigate = useNavigate();

    const breakpointColumns = {
        default: 4,
        1100: 3,
        700: 2,
        400: 1
    };

    // Fetch favorite and disliked recipes
    useEffect(() => {
        if (!sessionToken) return;
        
        const fetchFavoritesAndDislikes = async () => {
            try {
                // Fetch favorites
                const favoritesData = await getAllFavoriteRecipes(sessionToken);
                if (Array.isArray(favoritesData)) {
                    setFavoriteRecipes(favoritesData);
                }

                // Fetch dislikes
                const dislikesData = await getAllDislikedRecipes(sessionToken);
                if (Array.isArray(dislikesData)) {
                    setDislikedRecipes(dislikesData);
                }
            } catch (error) {
                console.error('Error fetching favorites/dislikes:', error);
            }
        };
        fetchFavoritesAndDislikes();
    }, [sessionToken, getAllFavoriteRecipes, getAllDislikedRecipes]);

    // Fetch pantry ingredients for pantry match calculation
    useEffect(() => {
        const fetchPantryIngredients = async () => {
            if (!sessionToken || !pgUser?.pantry_id || !pgUser?.id) return;
            
            try {
                const response = await fetchWithCSRF(`/api/v1/pantry-ingredient/pantry/${pgUser.pantry_id}/app_user/${pgUser.id}`, {
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (Array.isArray(data)) {
                        setPantryIngredients(data);
                    }
                }
            } catch (error) {
                console.error('Error fetching pantry ingredients:', error);
            }
        };

        fetchPantryIngredients();
    }, [sessionToken, pgUser?.pantry_id, pgUser?.id]);

    // Fetch personalized recipes
    useEffect(() => {
        const fetchRecipes = async () => {
            if (!sessionToken) {
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(`/api/v1/recipe/for-you/${pgUser.id}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });

                const json = await response.json();

                if (!response.ok) {
                    throw new Error(`Failed to fetch recipes: ${response.status}`);
                }

                if (Array.isArray(json)) {
                    setRecipes(json);
                } else {
                    console.error('Invalid data format:', json);
                    setRecipes([]);
                }
            } catch (error) {
                console.error('Error fetching recipes:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchRecipes();
    }, [sessionToken, pgUser?.id]);

    // Filter and sort recipes by pantry match percentage using useMemo
    const filteredAndSortedRecipes = useMemo(() => {
        let filtered = recipes;

        // Filter by search term
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = recipes.filter(recipe => {
                // Search by recipe name
                if (recipe.name?.toLowerCase().includes(searchLower)) {
                    return true;
                }
                
                // Search by description
                if (recipe.description?.toLowerCase().includes(searchLower)) {
                    return true;
                }
                
                // Search by ingredient names
                if (recipe.ingredients && Array.isArray(recipe.ingredients)) {
                    return recipe.ingredients.some(ingredient => 
                        ingredient.ingredient_name && 
                        ingredient.ingredient_name.toLowerCase().includes(searchLower)
                    );
                }
                
                return false;
            });
        }

        // Sort by pantry match percentage (descending)
        const sorted = [...filtered].sort((a, b) => {
            const matchA = calculatePantryMatch(pantryIngredients, a.ingredients || []);
            const matchB = calculatePantryMatch(pantryIngredients, b.ingredients || []);
            return matchB - matchA;
        });

        // Log the final sorted order
        const sortedWithPercentages = sorted.map(r => ({
            name: r.name,
            match: calculatePantryMatch(pantryIngredients, r.ingredients || []) + '%'
        }));
        console.log('[RecipesForPantryRankedByPantryMatch] Final sorted order (top 10):', sortedWithPercentages.slice(0, 10));
        console.log('[RecipesForPantryRankedByPantryMatch] Full sorted order:', sortedWithPercentages);
        
        return sorted;
    }, [recipes, searchTerm, pantryIngredients]);

    const handlePantryIngredientAdded = async () => {
        // Refetch pantry ingredients for pantry match calculation
        if (sessionToken && pgUser?.pantry_id && pgUser?.id) {
            try {
                const response = await fetchWithCSRF(`/api/v1/pantry-ingredient/pantry/${pgUser.pantry_id}/app_user/${pgUser.id}`, {
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (Array.isArray(data)) {
                        setPantryIngredients(data);
                    }
                }
            } catch (error) {
                console.error('Error fetching pantry ingredients:', error);
            }
        }
    };

    return (
        <ErrorBoundary fallbackMessage="Unable to load pantry recipes. Please try again later.">
            <div className="container">
                <div style={{ paddingLeft: '15px', paddingRight: '15px', marginBottom: '15px', marginTop: '25px', textAlign: 'left' }}>
                    <h2 style={{ marginBottom: '10px', textAlign: 'left' }}>Recipes Based on Your Pantry</h2>
                    {pgUser?.pantry_id && pgUser?.id && (
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <AddPantryIngredientButton
                                pantryId={pgUser.pantry_id}
                                appUserId={pgUser.id}
                                onSubmit={handlePantryIngredientAdded}
                                buttonText="+ Add Ingredient to Pantry"
                            />
                            {/* <button 
                                onClick={() => navigate('/pantry')}
                                // style={{ 
                                //     backgroundColor: '#52cd92', 
                                //     color: 'white', 
                                //     borderRadius: '24px', 
                                //     fontWeight: '500',
                                //     border: 'none',
                                //     padding: '8px 20px',
                                //     cursor: 'pointer'
                                // }}
                                className="btn btn-secondary"
                                style={{ borderRadius: '24px' }}
                            >
                                View Pantry
                            </button> */}
                        </div>
                    )}
                </div>
                {/* <p style={{ textAlign: 'left', paddingLeft: '15px' }}>
                    Personalized recipes ranked by how well they match your pantry
                </p> */}
                
                {loading ? (
                    <div className="text-center my-4">
                        <Spinner animation="border" role="status" style={{ color: '#52cd92' }}>
                            <span className="visually-hidden">Loading recipes...</span>
                        </Spinner>
                    </div>
                ) : filteredAndSortedRecipes.length === 0 && searchTerm ? (
                    <div className="text-center my-5">No recipes found matching "{searchTerm}"</div>
                ) : recipes.length === 0 ? (
                    <div className="alert alert-info" role="alert">
                        <p className="mb-0">No recipes available yet. Start exploring and cooking recipes to get personalized recommendations!</p>
                    </div>
                ) : (
                    <Masonry
                        breakpointCols={breakpointColumns}
                        className="my-masonry-grid"
                        columnClassName="my-masonry-grid_column"
                    >
                        {filteredAndSortedRecipes.map(recipe => (
                            <RecipeCard 
                                key={recipe.id} 
                                recipe={recipe} 
                                isFavorite={Array.isArray(favoriteRecipes) && favoriteRecipes.includes(Number(recipe.id))}
                                isDisliked={Array.isArray(dislikedRecipes) && dislikedRecipes.includes(Number(recipe.id))}
                                pantryIngredients={pantryIngredients}
                                onMealAdded={onMealAdded}
                            />
                        ))}
                    </Masonry>
                )}
            </div>
        </ErrorBoundary>
    );
};

export default RecipesForPantry;

